import { EventEmitter, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { Note } from '@core/objects/note';
import { User } from '@core/objects/user';

import WebViewer from '@pdftron/pdfjs-express';
import WebViewerInstance from '@pdftron/pdfjs-express';
import Annotations  from '@pdftron/pdfjs-express';

interface PdfjsViewerMenuOptions {
    annotations?: boolean;
}
  
interface PdfjsViewerNotesOptions {
    readonly: boolean;
}
  
// general parameters for the future: options for ui and more to add yet
export interface PdfjsViewerConfig {
    menu?: PdfjsViewerMenuOptions;
    notes?: PdfjsViewerNotesOptions; // not used yet
}

export interface PdfjsViewerAnnotationChanges {
    content: string,
    action: string,
    legacy: boolean
}

export class PdfjsViewer {
    private readonly assetsUrl: string = "assets/pdfjs/"; //  fixed for /share or /app in constructor

    private config: PdfjsViewerConfig = {
        menu: {
            annotations: true
        },
        notes: {
            readonly: false
        }
    };
    // private user : User = null;
    private authorName: string;
    private baseUrl: string;

    public handler: WebViewer;
    public instance: WebViewerInstance;

    public onDocumentReady = new EventEmitter();
    public onAnnotationsChange = new EventEmitter<PdfjsViewerAnnotationChanges>();

    private pdfSubscriber: Subscription;

    public constructor(config: PdfjsViewerConfig, private user: User, baseHref: string,private httpClient: HttpClient ) {
        this.config = {...this.config, ...config};
        // this.user = user;
        this.authorName = this.user.name == this.user.email ? this.user.email : this.user.name+' ('+this.user.email+')';
        this.baseUrl = baseHref + this.assetsUrl;
        console.log("viewer configuration-->", this.config, this.baseUrl);
    }

    public clear() {
        if(this.instance){
            this.instance.UI.closeElements([ 'toolsHeader', 'leftPanel' ]); // close tools opened
            this.instance.UI.setToolbarGroup(this.instance.UI.ToolbarGroup.VIEW); // set default toolbar sub menu to avoid load previous opened menu on next file
        }
        this.handler = null;
        if(this.pdfSubscriber)
            this.pdfSubscriber.unsubscribe();
    }

    public init(file: string, element: ElementRef) {
        this.docLoadedAction = this.docLoadedAction.bind(this);
        // this.annotationsChangedAction = this.annotationsChangedAction.bind(this);

        this.handler = WebViewer(
            {
                licenseKey: 'DlzZY8GwQ8ylwHFcqWGO',
                path: this.baseUrl,
                // initialDoc: file,
                // extension: 'pdf',
                disabledElements: [ 'toolsHeader'], // at beginning should be disabled to avoid show from previous execution.
                forceClientSideInit: true,
                // enableMeasurement: true
            },
            element.nativeElement).then(async ( instance: WebViewerInstance) => {
                this.instance = instance;   
                this.instance.UI.enableElements(['loadingModal']);
                this.instance.UI.openElements([ 'loadingModal' ]);
                this.instance.UI.closeElements([ 'toolsHeader', 'leftPanel' ]);
                // get pdf by our own httpclient and pass it as a blob to the webviewer to avoid credentials and cookie send issues.
                this.pdfSubscriber = this.httpClient.get(file, { responseType: 'blob' }).subscribe({
                    next: (response :any) => {
                        const file = new Blob([response], { type: 'application/pdf' });
                        this.instance.loadDocument(file,{ extension: 'pdf'});
                    },
                    error:(error:any) => {
                        console.log('Error getting pdf: ',error);
                        // TODO: maybe set notification failed here
                    }
                });

                this.configure(this.instance);

                // listeners
                // this.instance.docViewer.on('documentLoaded', this.docLoadedAction(instance));
                // this.instance.docViewer.on('documentLoaded', this.docLoadedAction);

                this.instance.Core.documentViewer.addEventListener("documentLoaded", this.docLoadedAction);

                // set current user:
                this.instance.Core.annotationManager.setCurrentUser(this.authorName);
                // instance.Core.annotationManager.addEventListener('annotationChanged', (annotations, action, { imported }) => this.annotationsChangedAction);
                // doc: Triggered when an annotation or annotations have been changed (added, deleted, modified)
                this.instance.Core.annotationManager.addEventListener('annotationChanged', (annotations, action, options) => this.annotationsChangedAction(annotations, action, options));

                this.instance.Core.documentViewer.addEventListener('annotationsLoaded', () => {
                    console.log("annotations loaded event");
                });

                
            }
        );
    }

    private configure(instance: WebViewerInstance) {
        // Alway disabled elements in UI
        // ENABLE / DISABLE features - this seems to rule over Elements
        // Contains string enums for all features for WebViewer UI, list of Features: https://pdfjs.express/api/UI.html#.Feature
        var Feature = instance.UI.Feature;
        instance.UI.setToolbarGroup(instance.UI.ToolbarGroup.VIEW); // set default toolbar sub menu to avoid load previous opened
        instance.UI.enableElements(['toolsHeader','toolbarGroup-Measure']); // enable it to be available after tool view is selected and changed
        // disabled undesired elements by default:
        instance.UI.disableElements(['toolbarGroup-Insert']);
        instance.UI.disableElements(['toolbarGroup-FillAndSign']);
        instance.UI.disableFeatures([Feature.Download, Feature.Print]); // mandatory disabled by now
        // examples to improve options.
        // instance.UI.disableTools([Tools.ToolNames.STICKY, Tools.ToolNames.FREETEXT]); // from https://pdfjs.express/api/Core.Tools.html#.ToolNames
        // instance.UI.disableFeatures([Feature.XXXX]);
        // instance.UI.disableToolDefaultStyleUpdateFromAnnotationPopup(); 
        // layers tools
        // instance.UI.enableElements(['layersPanelButton','layersPanel']);
        
        // instance.UI.setActiveLeftPanel('layersPanel');
        // for the future
        // let measurementManager = instance.Core.documentViewer.getMeasurementManager();
        // measure menu enabled at default
        instance.UI.enableFeatures([Feature.Measurement]);
        // configure measure tools to default in mm
        const {
            Tools
        } = this.instance.Core;

        // Define all distance measurement tool names
        // https://pdfjs.express/api/Core.Tools.html (not all are in documentation)
        const measurementToolNames = [
            // Distance measurement tools
            Tools.ToolNames.DISTANCE_MEASUREMENT,
            Tools.ToolNames.DISTANCE_MEASUREMENT2,
            Tools.ToolNames.DISTANCE_MEASUREMENT3,
            Tools.ToolNames.DISTANCE_MEASUREMENT4,
              // Perimeter measurement tools
            Tools.ToolNames.PERIMETER_MEASUREMENT,
            Tools.ToolNames.PERIMETER_MEASUREMENT2,
            Tools.ToolNames.PERIMETER_MEASUREMENT3,
            Tools.ToolNames.PERIMETER_MEASUREMENT4,
            // Area measurement tools
            Tools.ToolNames.AREA_MEASUREMENT,
            Tools.ToolNames.AREA_MEASUREMENT2,
            Tools.ToolNames.AREA_MEASUREMENT3,
            Tools.ToolNames.AREA_MEASUREMENT4,
            // Arc measurement tools
            Tools.ToolNames.ARC_MEASUREMENT,
            Tools.ToolNames.ARC_MEASUREMENT2,
            Tools.ToolNames.ARC_MEASUREMENT3,
            Tools.ToolNames.ARC_MEASUREMENT4,
            // Ellipse measurement tools
            Tools.ToolNames.ELLIPSE_MEASUREMENT,
            Tools.ToolNames.ELLIPSE_MEASUREMENT2,
            Tools.ToolNames.ELLIPSE_MEASUREMENT3,
            Tools.ToolNames.ELLIPSE_MEASUREMENT4,
            // Cloudy rectangular area measurement tools
            Tools.ToolNames.CLOUDY_RECTANGULAR_AREA_MEASUREMENT,
            Tools.ToolNames.CLOUDY_RECTANGULAR_AREA_MEASUREMENT2,
            Tools.ToolNames.CLOUDY_RECTANGULAR_AREA_MEASUREMENT3,
            Tools.ToolNames.CLOUDY_RECTANGULAR_AREA_MEASUREMENT4,
            // Rectangular area measurement tools
            Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT,
            Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT2,
            Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT3,
            Tools.ToolNames.RECTANGULAR_AREA_MEASUREMENT4
        ];

        // Iterate through each measurement tool to set default to metric system units and mm units by default
        measurementToolNames.forEach(toolName => {
            const measureTool = this.instance.Core.documentViewer.getTool(toolName);

            if (measureTool && measureTool.defaults) {
                // Set default measurement system and units to Metric for each tool
                measureTool.defaults.MeasurementSystems = "Metric";
                measureTool.defaults.MeasurementUnits = "mm";
                measureTool.defaults.Scale = [
                    [1, 'mm'],   // Set scale to use millimeters
                    [1, 'mm']
                ];
            }
        });
        // Disable elements based on config values
        // BE able to hide the Annotate, Shapes, Edit and Insert, panels, buttons etc & toolbar groups. Link: https://docs.apryse.com/documentation/web/guides/customizing-header/
        if (!this.config.menu?.annotations) {
            instance.UI.disableElements(['toolbarGroup-Annotate',
                                        'toolbarGroup-Shapes',
                                        'toolbarGroup-Insert',
                                        'toolbarGroup-Edit']);
            // we must disable context menu as well  to avoid place notes button on left clickmenu.
            // disable (leftPanel/overlay/popup/modal) elements.
            instance.disableElements([ 'contextMenuPopup','textPopup' ]); // disable context menu and text menu

            // AVOID REPLY action
            this.instance.UI.disableReplyForAnnotations((annotation) => {
                return true;
            });

            // DISABLE edito note and update status menu
            instance.disableElements([ 'noteState','notePopup' ]); 
    
        }
        /* else { // removed Fill custom menu by Diego 10/7/2023
            let items = [];
            // modify header menu to put callouts in new custom menu from items in fill and sign called Fill
            instance.UI.setHeaderItems(function(header) {
                // items in toolbarGroup are: spacer,  1st item, 2nd item, ... etcetcetc    

                // Quit the undesired items  from the 'toolbarGroup-FillAndSign' items list
                const stampBtn = header.getHeader('toolbarGroup-FillAndSign').getItems().splice(6, 1)[0];         
                const signBtn = header.getHeader('toolbarGroup-FillAndSign').getItems().splice(1, 1)[0];    
                // Add the calloutToolButton from 'toolbarGroup-Insert' to the 'toolbarGroup-FillAndSign'
                const calloutItem = header.getHeader('toolbarGroup-Insert').getItems().splice(5, 1)[0];
                header.getHeader('toolbarGroup-FillAndSign').push(calloutItem);
                // get remaining desided items from the header FillAndSign
                items = header.getHeader('toolbarGroup-FillAndSign').getItems();
            });   
            // create our custom toolbar 
            this.createToolbar(instance,"Fill",items);  
        }*/
    }

    private docLoadedAction() : void {
        console.log("docLoadedAction");

        this.instance.UI.closeElements([ 'loadingModal' ]);
        // console.log(this.instance.Core.documentViewer.getDocument().getPageInfo(1));
        this.onDocumentReady.emit();
        return;
    }

    private async annotationsChangedAction(annotations, action, options) {
        if(options.imported || options?.source == 'clear' || this.config.notes?.readonly ) return;
        console.log("annotationsChanged action", annotations, action, options);
        console.log("annotations changed event");
        console.log("action--->", action);
        console.log("imported--->", options.imported);
        console.log("author--->", annotations[0].Author);
        console.log("annotation-->", annotations[0]);
        
        if(action == "add" && annotations[0].getCustomData('TwonaUserId') == "" ) {

            annotations[0].Author = this.authorName;
            annotations[0].setCustomData("TwonaUserId", this.user.id);
        } 
        // TODO mange delete annotations
        // console.log(this.instance.Core.documentViewer.getDocument().getPageInfo(1));
        const xfdfString = await this.instance.Core.annotationManager.exportAnnotations(
            {annotList: [annotations[0]]}
        );
        // devel purposes only:
        // const AllXfdfString = await this.instance.Core.annotationManager.exportAnnotations();
        // this.onAnnotationsChange.emit(xfdfString);
        // emit event checking either is legacy note (imported) or new xfdf annotation
        this.onAnnotationsChange.emit({ content: xfdfString, action: action, legacy: (annotations[0].getCustomData('TwonaNoteId') != "")});
    }

    // this import old notes one by one to xfdf format. 
    public importLegacyNotes(notes: Array<Note>) {
        if(notes.length == 0) return;
        
        const {
            Annotations,
            annotationManager,
            documentViewer
        } = this.instance.Core;

        // If there are already annotations, the import has been done yet and we have to clear any new notes added that trigger an error
        if(this.instance.Core.annotationManager.getAnnotationsList()?.length > 0){
            this.instance.Core.annotationManager.deleteAnnotations( this.instance.Core.annotationManager.getAnnotationsList(), {source: 'clear'});
            return; // let's check if all imported annotations are notes number
        }

        let stickies = notes.map((note: Note) => {
            // get width and height
            let pi = documentViewer.getDocument().getPageInfo(note.page);
            console.log("page info", pi);

            let sticky = new this.instance.Core.Annotations.StickyAnnotation();
            sticky.X = note.options.x*pi.width;
            sticky.Y = note.options.y*pi.height;
            // build author name from old note's user
            let noteAuthorName = note.user.name == note.user.email ? note.user.email : note.user.name+' ('+note.user.email+')';
            sticky.Author = noteAuthorName;
            sticky.PageNumber = note.page;
            sticky.DateCreated = note.date_created;
            // Maybe not needed when current user is set on annotation manager.
            sticky.Locked = this.user.id != note.user.id;
            sticky.ReadOnly = this.user.id != note.user.id;
            sticky.LockedContents = this.user.id != note.user.id;
            sticky.setContents(note.description);
            sticky.setCustomData('TwonaUserId', note.user.id);
            sticky.setCustomData('TwonaNoteId', note.id);
            console.log("sticky", sticky);
            return sticky;
        });

        stickies.forEach((sticky: Annotations.StickyAnnotation) => { 
            this.instance.Core.annotationManager.addAnnotation(sticky);
            this.instance.Core.annotationManager.redrawAnnotation(sticky);
        });
        // set the annotationMannager as readonly after import the notes to avoid replyes, accepted/rejected marks etc
        if(this.config.notes?.readonly){ 
            // this.instance.Core.annotationManager.setReadOnly(true); // DOESN'T WORK FOR MEASURE TOOLS <deigo@twonas.com> 13/2/2024
            // AVOID REPLY action
            this.instance.UI.disableReplyForAnnotations((annotation) => {
                return true;
            });
            // HIDE button for update note status
            this.instance.disableElements([ 'noteState','notePopup' ]); // disable context menu for update note status
        }



    }

    public async importXfdfAnnotations(xfdfAnnotations: string) {
        const {
            annotationManager,
            documentViewer
        } = this.instance.Core;

        try {
            this.instance.Core.annotationManager.deleteAnnotations( this.instance.Core.annotationManager.getAnnotationsList(), {source: 'clear'});
            this.instance.Core.annotationManager.importAnnotations(xfdfAnnotations);
            // set the annotationMannager as readonly after import the notes to avoid replies, accepted/rejected marks etc
            if(this.config.notes?.readonly){ 
                // this.instance.Core.annotationManager.setReadOnly(true); // DOESN'T WORK FOR MEASURE TOOLS <deigo@twonas.com> 13/2/2024 
                // AVOID REPLY action
                this.instance.UI.disableReplyForAnnotations((annotation) => {
                    return true;
                });
                // AVOID REPLY action
                this.instance.disableElements([ 'noteState','notePopup' ]); // disable context menu for update note status

            }
            
        } catch (error) {
            console.log(" error importing annotations XFDF: ",error)
        }
    }


    // protected update() {
    //     let nsize: any = {};
    //     for(const key in this.viewers) {
    //         nsize = this.viewers[key].getNormalizeSize();

    //         // this.status[key].setContainerSize(nsize.width, nsize.height);
    //         // No es necesario aplicar el size normalizado
    //         // ya que el click se hace sobre la imagen y recupera los valores
    //         // reales de dimensiones
    //         // PROPUESTA: NotesViewerStatus puede ser una interfaz que cumpla
    //         // el contrato de Viewer (patrón Repository)
    //         this.status[key].setContainerSize(this.viewers[key].size.width, this.viewers[key].size.height);
    //         this.status[key].setRotation(this.viewers[key].rotation);
    //     }

    //     this.onChange.emit(this);
    // }

    /**
     * 
     * Custom function to create a custom toolbar
     * @param instance the webviewer instance of this pdf preview
     * @param toolbarName the name of the toolbar to create,
     * @param items The items inside the toolbar. 
     * @param defaultItems boolean indicates either the default items should be included (true) or not
     * Documentation here: https://pdfjs.express/api/UI.html#.createToolbarGroup
     */
    private createToolbar(instance: WebViewerInstance, toolbarName:string, items: Array<Object>, defaultItems: boolean = false  ) : void {
        //create new toolbar group:
        instance.UI.createToolbarGroup({
            name: toolbarName,
            dataElementSuffix: toolbarName,
            useDefaultElements: defaultItems,
            children: items
        });
        

    }
}